import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { formatEuroCents } from '../lib/format';
import { BanknoteIcon, CardTerminalIcon } from '../admin/NavIcons';
import KartenStatus from './KartenStatus';
import { eventGeschlossen, useKartenzahlung } from './useKartenzahlung';

// Für die Tests, die die Zeiten von hier beziehen.
export { ERFOLG_MS, NACHFRAGE_MS } from './useKartenzahlung';

/**
 * Sammelgast „Abrechnen" (2026-09-17, Owner-Idee). Zeigt, was der Gast vor diesem
 * Tablet seit der letzten Abrechnung bestellt hat, und trägt es mit „Bezahlt – neuer
 * Gast" als bezahlt ein.
 *
 * Bewusst ein Dialog statt eines einzelnen „Neuer Gast"-Knopfes: Der Tipp trägt Geld
 * als erhalten ein. Wer vorher sieht, was er kassiert, tippt nicht aus Versehen.
 *
 * Gesendet wird, was der Dialog ANGEZEIGT hat (`throughSk`, `totalCents`). Hat sich
 * die Bestellung inzwischen geändert, lehnt der Server ab (412) und der Dialog lädt
 * neu -- kassiert wurde der angezeigte Betrag, nicht irgendein anderer.
 *
 * 2026-10-14 (v0.17 Stufe 2): **Pfand** und **„Mit Karte"**.
 *  - Pfand (D-11, FR-272): getippt wird nur die Anzahl, je Stück 1,00 €. Der Dialog zeigt
 *    Bestellung + Pfand = zu zahlen -- für bar wie für Karte. Das Pfand ist KEINE Buchung: Der
 *    Barweg schickt weiter nur die Bestellung, der Kartenweg schickt die Anzahl mit, damit der
 *    Server den Betrag am Terminal selbst rechnet.
 *  - Karte (FR-255–FR-259): Der Dialog wartet, bis der SERVER bei SumUp ein Ergebnis gelesen hat
 *    (ADR-0009 §1). „Abbrechen" ist nur eine Bitte an das Terminal -- danach wird weiter gefragt,
 *    und ging die Karte doch durch, sagt der Dialog es (AC-241).
 *
 * 2026-10-15 (Stufe 3): Starten, Nachfragen und Abbrechen leben in `useKartenzahlung`, die
 * Anzeige in `KartenStatus` -- dieselben wie bei der Zahlung einer Person.
 */

interface OrderLine {
  beverageId: string;
  name: string;
  unitPriceCents: number;
  quantity: number;
  amountCents: number;
}

interface GuestOrder {
  lines: OrderLine[];
  totalCents: number;
  throughSk?: string;
}

/** Fest im Code, wie im Server (D-11). */
export const PFAND_CENTS = 100;
const MAX_PFAND = 50;

export default function SammelgastSettleDialog({
  kartenzahlung = false,
  terminalName,
  onClose,
  onSettled,
  onEventClosed,
  onDeviceInvalid,
}: {
  /** Vom Server gesagt (`/kiosk/session`): Terminal am Tablet und SumUp eingerichtet. */
  kartenzahlung?: boolean;
  /**
   * 2026-10-15 (Owner-Test): der Name des Terminals, an das der Betrag geht. Mit zwei Terminals am
   * Stand fällt so sofort auf, wenn das Tablet am falschen hängt.
   */
  terminalName?: string;
  onClose: () => void;
  onSettled: (balanceCents: number) => void;
  onEventClosed: () => void;
  onDeviceInvalid: () => void;
}) {
  const [order, setOrder] = useState<GuestOrder>();
  const [pfand, setPfand] = useState(0);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const backButtonRef = useRef<HTMLButtonElement>(null);

  /**
   * 2026-10-15 (Owner-Test, Fehler von mir): Die Rückruffunktionen des Kiosks über EINE Ref.
   *
   * Der Kiosk erzeugt sie bei jedem Zeichnen neu, und er zeichnet mit dem Rückgängig-Countdown
   * nach jeder Buchung zwei Minuten lang jede Sekunde. Als Abhängigkeiten von `useCallback` machten
   * sie `handleAuthErrors` und `load` jede Sekunde neu -- mit zwei Folgen:
   *  - das Nachfragen bei einer Kartenzahlung (alle 2 s) startete jede Sekunde von vorn und fragte
   *    NIE, bis der Countdown ablief; die Zahlung war längst verbucht, das Pop-up wartete weiter;
   *  - die Bestellung wurde, solange das Pop-up offen war, jede Sekunde neu geladen (seit 17.09.).
   */
  const rueckrufe = useRef({ onSettled, onEventClosed, onDeviceInvalid });
  useEffect(() => {
    rueckrufe.current = { onSettled, onEventClosed, onDeviceInvalid };
  });

  const handleAuthErrors = useCallback((e: unknown): boolean => {
    if (eventGeschlossen(e)) {
      rueckrufe.current.onEventClosed();
      return true;
    }
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      rueckrufe.current.onDeviceInvalid();
      return true;
    }
    return false;
  }, []);

  const karte = useKartenzahlung({
    onAuthError: handleAuthErrors,
    onErfolg: (balanceCents) => rueckrufe.current.onSettled(balanceCents),
  });
  const { phase } = karte;

  const load = useCallback(async () => {
    setError(undefined);
    try {
      setOrder(await api.get<GuestOrder>('/kiosk/sammelgast/order', 'kiosk'));
    } catch (e) {
      if (!handleAuthErrors(e)) setError('Bestellung konnte nicht geladen werden.');
    }
  }, [handleAuthErrors]);

  useEffect(() => {
    void load();
  }, [load]);

  // Fokus auf „Zurück": ein versehentliches Enter soll nichts als bezahlt eintragen.
  useEffect(() => {
    backButtonRef.current?.focus();
  }, []);

  const bar = async () => {
    if (!order?.throughSk) return;
    setSubmitting(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const { balanceCents } = await api.post<{ balanceCents: number }>(
        '/kiosk/sammelgast/settle',
        { throughSk: order.throughSk, totalCents: order.totalCents },
        'kiosk',
      );
      onSettled(balanceCents);
    } catch (e) {
      if (e instanceof ApiError && e.status === 412) {
        setNotice('Die Bestellung hat sich geändert. Bitte die Summe prüfen.');
        karte.zurueck();
        await load();
      } else if (e instanceof ApiError && e.status === 409 && !eventGeschlossen(e)) {
        setError(e.message);
      } else if (!handleAuthErrors(e)) {
        setError('Abrechnen fehlgeschlagen. Bitte erneut versuchen.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const mitKarte = async () => {
    if (!order?.throughSk) return;
    setSubmitting(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const ergebnis = await karte.starte({
        throughSk: order.throughSk,
        totalCents: order.totalCents,
        pfandStueck: pfand,
      });
      if (ergebnis.art === 'geaendert') {
        setNotice('Die Bestellung hat sich geändert. Bitte die Summe prüfen.');
        await load();
      } else if (ergebnis.art === 'fehler') {
        setError(ergebnis.text);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const empty = order !== undefined && order.throughSk === undefined;
  const zuZahlen = (order?.totalCents ?? 0) + pfand * PFAND_CENTS;
  // Solange die Karte in der Luft ist, schließt ein Tipp daneben nichts -- sonst wüsste niemand mehr,
  // wie die Zahlung ausging.
  const schliessbar =
    phase.art === 'bereit' || phase.art === 'gescheitert' || phase.art === 'ohneZuordnung';

  return (
    <div
      className="confirm-overlay"
      role="presentation"
      onClick={() => {
        if (schliessbar) onClose();
      }}
    >
      <div
        className="confirm-dialog kiosk-settle-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Abrechnen"
        onClick={(e) => e.stopPropagation()}
      >
        <h2>Abrechnen</h2>
        {notice && <div className="kiosk-settle-notice">{notice}</div>}
        {error && <div className="error-banner">{error}</div>}

        <KartenStatus
          phase={phase}
          terminalName={terminalName}
          backButtonRef={backButtonRef}
          onAbbrechen={(z) => void karte.abbrechen(z)}
          onErneutNachfragen={karte.erneutNachfragen}
          onKeineAntwortAbbrechen={() => {
            karte.zurueck();
            void load();
          }}
          onZurueck={onClose}
        />

        {(phase.art === 'bereit' || phase.art === 'gescheitert') && (
          <>
            {phase.art === 'gescheitert' && <div className="error-banner">{phase.grund}</div>}
            {order === undefined && !error && <p>Lädt…</p>}
            {empty && <p>Keine offenen Buchungen.</p>}
            {order && !empty && (
              <table className="kiosk-settle-table">
                <tbody>
                  {order.lines.map((l) => (
                    <tr key={`${l.beverageId}#${l.name}#${l.unitPriceCents}`}>
                      <td className="kiosk-settle-qty">{l.quantity}×</td>
                      <td>{l.name}</td>
                      <td className="kiosk-settle-amount">{formatEuroCents(l.amountCents)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  {/* „Bestellung" statt „Gesamt": Darunter steht seit dem Pfand ein zweiter
                    Betrag, und zwei Zeilen, die beide nach „alles" klingen, liest man falsch. */}
                  <tr className="kiosk-settle-bestellung">
                    <td colSpan={2}>Bestellung</td>
                    <td className="kiosk-settle-amount">{formatEuroCents(order.totalCents)}</td>
                  </tr>
                  <tr className="kiosk-settle-pfand">
                    <td colSpan={2}>
                      <span className="kiosk-pfand-stepper">
                        Pfand:
                        <button
                          type="button"
                          aria-label="Pfand weniger"
                          disabled={pfand === 0}
                          onClick={() => setPfand((n) => Math.max(0, n - 1))}
                        >
                          −
                        </button>
                        <span aria-label="Pfand Anzahl">{pfand}</span>
                        <button
                          type="button"
                          aria-label="Pfand mehr"
                          disabled={pfand >= MAX_PFAND}
                          onClick={() => setPfand((n) => Math.min(MAX_PFAND, n + 1))}
                        >
                          +
                        </button>
                      </span>
                    </td>
                    <td className="kiosk-settle-amount">{formatEuroCents(pfand * PFAND_CENTS)}</td>
                  </tr>
                  <tr className="kiosk-settle-zuzahlen">
                    <td colSpan={2}>Zu zahlen</td>
                    <td className="kiosk-settle-amount">{formatEuroCents(zuZahlen)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
            <div className="confirm-dialog-actions">
              <button ref={backButtonRef} onClick={onClose}>
                Zurück
              </button>
              {kartenzahlung && (
                <button
                  className="primary-button"
                  disabled={!order || empty || submitting}
                  onClick={() => void mitKarte()}
                >
                  <CardTerminalIcon />
                  {phase.art === 'gescheitert' ? 'Erneut mit Karte' : 'Mit Karte'}
                </button>
              )}
              <button
                className="primary-button"
                disabled={!order || empty || submitting}
                onClick={() => void bar()}
              >
                {/* 2026-10-18: Der Schein nur, wo das Wort „Bar" dasteht -- ohne Terminal ist
                    „Bezahlt" der einzige Weg, da unterscheidet ein Bild nichts. */}
                {kartenzahlung && <BanknoteIcon />}
                {kartenzahlung ? 'Bar bezahlt – neuer Gast' : 'Bezahlt – neuer Gast'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
